import React from 'react';
import { motion } from 'framer-motion';
import { Globe, MapPin, AlertTriangle, CheckCircle, Lock, Sparkles, Crown } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface EthnicityAnalysisProps {
  isPremium: boolean;
  onUpgrade: () => void;
  ethnicityData: any;
}

const EthnicityAnalysis: React.FC<EthnicityAnalysisProps> = ({ isPremium, onUpgrade, ethnicityData }) => {
  // Generate blend-in locations based on predicted ethnicity
  const generateBlendInLocations = () => {
    const baseCountry = ethnicityData.primary.country;
    
    // Dynamic blend-in locations based on ethnicity
    const locationMaps: { [key: string]: any[] } = {
      "Lebanon": [
        { location: "Lebanon", flag: "🇱🇧", reason: "Perfect match - you'll be indistinguishable from locals", score: 10 },
        { location: "Syria", flag: "🇸🇾", reason: "Very similar features to local population", score: 9.5 },
        { location: "Turkey", flag: "🇹🇷", reason: "Mediterranean features blend seamlessly", score: 9.2 },
        { location: "Greece", flag: "🇬🇷", reason: "Mediterranean look is common here", score: 9.0 },
        { location: "Southern Italy", flag: "🇮🇹", reason: "Similar complexion and features", score: 8.8 },
        { location: "Spain", flag: "🇪🇸", reason: "Mediterranean appearance fits well", score: 8.5 },
        { location: "Portugal", flag: "🇵🇹", reason: "Olive skin tone is common", score: 8.3 },
        { location: "Argentina", flag: "🇦🇷", reason: "Large Middle Eastern diaspora", score: 8.0 }
      ],
      "Mexico": [
        { location: "Mexico", flag: "🇲🇽", reason: "Perfect match - you'll blend in completely", score: 10 },
        { location: "Colombia", flag: "🇨🇴", reason: "Very similar features and complexion", score: 9.5 },
        { location: "Spain", flag: "🇪🇸", reason: "Shared cultural and genetic heritage", score: 9.0 },
        { location: "Argentina", flag: "🇦🇷", reason: "Large Latin American population", score: 8.8 },
        { location: "Southern USA", flag: "🇺🇸", reason: "High Hispanic population areas", score: 8.5 },
        { location: "Peru", flag: "🇵🇪", reason: "Similar indigenous features", score: 8.3 },
        { location: "Philippines", flag: "🇵🇭", reason: "Spanish colonial influence", score: 8.0 },
        { location: "Brazil", flag: "🇧🇷", reason: "Diverse population with similar features", score: 7.8 }
      ],
      "India": [
        { location: "India", flag: "🇮🇳", reason: "Perfect match - you'll blend in completely", score: 10 },
        { location: "Pakistan", flag: "🇵🇰", reason: "Very similar features and complexion", score: 9.5 },
        { location: "Bangladesh", flag: "🇧🇩", reason: "Shared genetic heritage", score: 9.2 },
        { location: "Sri Lanka", flag: "🇱🇰", reason: "Similar South Asian features", score: 9.0 },
        { location: "Nepal", flag: "🇳🇵", reason: "Common features in region", score: 8.8 },
        { location: "UK (certain areas)", flag: "🇬🇧", reason: "Large South Asian diaspora", score: 8.5 },
        { location: "UAE", flag: "🇦🇪", reason: "Significant Indian population", score: 8.3 },
        { location: "Singapore", flag: "🇸🇬", reason: "Diverse with large Indian community", score: 8.0 }
      ]
    };

    return locationMaps[baseCountry] || locationMaps["Lebanon"];
  };

  const blendInLocations = generateBlendInLocations();
  const primaryLocation = blendInLocations[0];
  const lockedLocations = blendInLocations.slice(1);

  const challengingLocations = [
    { 
      location: "Japan", 
      flag: "🇯🇵", 
      severity: "High",
      reason: "Homogeneous society - foreigners stand out significantly. May face curiosity and occasional exclusion.",
      advice: "Learn Japanese language and customs to gain respect"
    },
    { 
      location: "South Korea", 
      flag: "🇰🇷", 
      severity: "High",
      reason: "Strong ethnic homogeneity. Non-Koreans may experience social barriers and workplace discrimination.",
      advice: "Build strong local connections and demonstrate cultural knowledge"
    },
    { 
      location: "China (rural areas)", 
      flag: "🇨🇳", 
      severity: "Medium-High",
      reason: "In rural areas, foreigners are rare and may face staring, unwanted attention, or mistrust.",
      advice: "Stick to major cities where diversity is more accepted"
    },
    { 
      location: "Poland", 
      flag: "🇵🇱", 
      severity: "Medium",
      reason: "Increasing nationalism. Middle Eastern appearance may attract negative attention in some areas.",
      advice: "Stay in cosmopolitan areas like Warsaw or Krakow"
    },
    { 
      location: "Hungary", 
      flag: "🇭🇺", 
      severity: "Medium",
      reason: "Rising anti-immigrant sentiment. May face prejudice in certain neighborhoods.",
      advice: "Be aware of political climate and avoid confrontational situations"
    },
    { 
      location: "Russia (outside Moscow)", 
      flag: "🇷🇺", 
      severity: "Medium-High",
      reason: "Ethnic tensions exist. Non-Slavic appearance may attract police attention or discrimination.",
      advice: "Always carry proper documentation and stay in tourist areas"
    }
  ];

  if (!isPremium) {
    return (
      <div className="space-y-6">
        {/* Free Preview - Primary Ethnicity Only */}
        <Card className="p-8 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200">
          <div className="flex items-center gap-3 mb-6">
            <Globe className="h-8 w-8 text-blue-600" />
            <h3 className="text-2xl font-bold text-gray-800">Predicted Ethnicity</h3>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 mb-6 shadow-lg"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <span className="text-6xl">{ethnicityData.primary.flag}</span>
                <div>
                  <h4 className="text-3xl font-bold text-gray-800">{ethnicityData.primary.country}</h4>
                  <p className="text-lg text-gray-600">{ethnicityData.primary.region}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-blue-600">{ethnicityData.primary.confidence}%</p>
                <p className="text-sm text-gray-600">Confidence</p>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>AI Analysis:</strong> Your facial features, bone structure, and complexion strongly suggest {ethnicityData.primary.country} or {ethnicityData.primary.region} ancestry.
              </p>
            </div>
          </motion.div>

          {/* Locked Alternative Predictions */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white z-10 flex items-end justify-center pb-6">
              <div 
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-2xl shadow-2xl cursor-pointer hover:scale-105 transition-all"
                onClick={onUpgrade}
              >
                <div className="text-center">
                  <Crown className="h-8 w-8 mx-auto mb-2" />
                  <p className="font-bold text-lg">Unlock 4 Alternative Predictions</p>
                  <p className="text-sm opacity-90">See other possible ethnicities</p>
                </div>
              </div>
            </div>
            
            <div className="blur-sm opacity-50">
              <h4 className="font-semibold text-lg mb-4 text-gray-700">Alternative Predictions:</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {ethnicityData.alternatives.map((alt: any, index: number) => (
                  <div key={index} className="bg-white rounded-xl p-4 text-center shadow-md">
                    <span className="text-3xl mb-2 block">{alt.flag}</span>
                    <p className="font-semibold text-gray-800">{alt.country}</p>
                    <p className="text-sm text-gray-600">{alt.confidence}%</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Locked Blend In Section */}
        <Card 
          className="p-8 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 cursor-pointer hover:shadow-xl transition-all"
          onClick={onUpgrade}
        >
          <div className="text-center">
            <Lock className="h-16 w-16 text-purple-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-purple-900 mb-3">
              🌍 Unlock Cultural Compatibility Analysis
            </h3>
            <p className="text-gray-700 mb-4 text-lg">
              Discover 8+ countries where you'll blend in as a local and places where you might face challenges.
            </p>
            <div className="bg-white rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">Premium features include:</p>
              <ul className="text-left space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>8+ countries where you'll blend in perfectly</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Honest analysis of challenging locations</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Cultural adaptation tips for each region</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>Safety advice for difficult areas</span>
                </li>
              </ul>
            </div>
            <p className="text-purple-600 font-semibold">
              Click to unlock for just $9.99
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Ethnicity Prediction - Premium */}
      <Card className="p-8 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200">
        <div className="flex items-center gap-3 mb-6">
          <Globe className="h-8 w-8 text-blue-600" />
          <h3 className="text-2xl font-bold text-gray-800">Predicted Ethnicity</h3>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-6 mb-6 shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <span className="text-6xl">{ethnicityData.primary.flag}</span>
              <div>
                <h4 className="text-3xl font-bold text-gray-800">{ethnicityData.primary.country}</h4>
                <p className="text-lg text-gray-600">{ethnicityData.primary.region}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-4xl font-bold text-blue-600">{ethnicityData.primary.confidence}%</p>
              <p className="text-sm text-gray-600">Confidence</p>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>AI Analysis:</strong> Your facial features, bone structure, and complexion strongly suggest {ethnicityData.primary.country} or {ethnicityData.primary.region} ancestry. 
              This includes characteristics common in this region.
            </p>
          </div>
        </motion.div>

        <div>
          <h4 className="font-semibold text-lg mb-4 text-gray-700">Alternative Predictions:</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ethnicityData.alternatives.map((alt: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-4 text-center shadow-md"
              >
                <span className="text-3xl mb-2 block">{alt.flag}</span>
                <p className="font-semibold text-gray-800">{alt.country}</p>
                <p className="text-sm text-gray-600">{alt.confidence}%</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Card>

      {/* Blend In Locations - Premium */}
      <Card className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle className="h-8 w-8 text-green-600" />
          <h3 className="text-2xl font-bold text-gray-800">Where You'll Blend In</h3>
        </div>
        <p className="text-gray-600 mb-6">
          Countries where you'll look like a local and experience minimal cultural barriers:
        </p>

        <div className="space-y-4">
          {blendInLocations.map((location, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{location.flag}</span>
                  <div>
                    <h4 className="font-bold text-lg text-gray-800">{location.location}</h4>
                    <p className="text-sm text-gray-600">{location.reason}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">{location.score}/10</p>
                  <p className="text-xs text-gray-500">Blend Score</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Card>

      {/* Challenging Locations - Premium */}
      <Card className="p-8 bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200">
        <div className="flex items-center gap-3 mb-6">
          <AlertTriangle className="h-8 w-8 text-orange-600" />
          <h3 className="text-2xl font-bold text-gray-800">Challenging Locations</h3>
        </div>
        <p className="text-gray-600 mb-6">
          <strong>Honest analysis:</strong> Places where you may face discrimination, prejudice, or social challenges due to appearance:
        </p>

        <div className="space-y-4">
          {challengingLocations.map((location, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl p-5 shadow-md border-l-4 border-orange-400"
            >
              <div className="flex items-start gap-3 mb-3">
                <span className="text-3xl">{location.flag}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-lg text-gray-800">{location.location}</h4>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                      location.severity === 'High' 
                        ? 'bg-red-100 text-red-700' 
                        : location.severity === 'Medium-High'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {location.severity} Risk
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{location.reason}</p>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-900">
                      <strong>💡 Advice:</strong> {location.advice}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 bg-white rounded-xl p-5 border-2 border-orange-300">
          <div className="flex items-start gap-3">
            <Sparkles className="h-6 w-6 text-orange-600 mt-1" />
            <div>
              <h4 className="font-bold text-gray-800 mb-2">Important Note</h4>
              <p className="text-sm text-gray-700 leading-relaxed">
                This analysis is based on documented experiences and social research. Individual experiences vary greatly. 
                Many people of all backgrounds travel safely to these locations. This information is meant to help you 
                prepare and make informed decisions, not to discourage travel. Cultural awareness and respect go a long way 
                in any destination.
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default EthnicityAnalysis;